import { useState, useEffect } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import {
  LayoutDashboard, Activity, Users, ShieldCheck, UserPlus, ScrollText,
  LogOut, Menu, X, Building2, Crown, CalendarDays, FlaskConical, Plane, Car,
  CreditCard, MessageSquare, BarChart3, Scale, Route as RouteIcon, Wallet, TrendingUp,
  Tags, Calculator, Receipt, Banknote,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/i18n/I18nProvider";
import { Button } from "@/components/ui/button";
import { ROLE_LABELS } from "@/lib/constants";
import { NotificationBell } from "@/components/NotificationBell";

const NAV_KEYS = {
  "/owner": "owner", "/operations": "operations", "/patients": "patients",
  "/appointments": "appointments", "/laboratory": "laboratory", "/travel": "travel",
  "/transportation": "transportation", "/payments": "payments", "/cash-drawer": "cashDrawer",
  "/exchange-rates": "exchangeRates", "/pricing": "pricing", "/accounting": "accounting",
  "/payables": "payables", "/finance": "finance", "/approvals": "approvals",
  "/journey": "journey", "/messages": "messages", "/reports": "reports",
  "/employees": "employees", "/permissions": "permissions", "/audit": "audit",
};

function buildNav(user, can) {
  const items = [];
  if (can("revenue", "view"))
    items.push({ to: "/owner", label: "Owner Dashboard", icon: LayoutDashboard });
  if (can("patient_records", "view"))
    items.push({ to: "/operations", label: "Operations", icon: Activity });
  if (can("patient_records", "view"))
    items.push({ to: "/patients", label: "Patients", icon: Users });
  if (can("appointments", "view"))
    items.push({ to: "/appointments", label: "Appointments", icon: CalendarDays });
  if (can("laboratory", "view"))
    items.push({ to: "/laboratory", label: "Laboratory", icon: FlaskConical });
  if (can("travel", "view"))
    items.push({ to: "/travel", label: "Travel", icon: Plane });
  if (can("transportation", "view"))
    items.push({ to: "/transportation", label: "Transportation", icon: Car });
  if (can("financial_records", "view"))
    items.push({ to: "/payments", label: "Payments", icon: CreditCard });
  if (["translator_cashier", "owner", "operations_manager", "general_accountant"].includes(user?.role))
    items.push({ to: "/cash-drawer", label: "Cash Drawer", icon: Wallet });
  if (["owner", "operations_manager", "general_accountant"].includes(user?.role))
    items.push({ to: "/exchange-rates", label: "Exchange Rates", icon: TrendingUp });
  const acctRoles = ["owner", "operations_manager", "supervising_dentist", "general_accountant"];
  if (acctRoles.includes(user?.role)) {
    items.push({ to: "/pricing", label: "Pricing & Costs", icon: Tags });
    items.push({ to: "/accounting", label: "Accounting", icon: Calculator });
    items.push({ to: "/payables", label: "Payables", icon: Receipt });
    items.push({ to: "/finance", label: "Bank & Expenses", icon: Banknote });
  }
  if (can("discounts_refunds", "view"))
    items.push({ to: "/approvals", label: "Discounts & Refunds", icon: Scale });
  const mgmtRoles = ["owner", "operations_manager", "supervising_dentist", "general_accountant", "coordination"];
  if (mgmtRoles.includes(user?.role))
    items.push({ to: "/journey", label: "Patient Journey", icon: RouteIcon });
  items.push({ to: "/messages", label: "Messages", icon: MessageSquare });
  if (can("reports", "view"))
    items.push({ to: "/reports", label: "Reports", icon: BarChart3 });
  if (["owner", "operations_manager"].includes(user.role))
    items.push({ to: "/employees", label: "Employees", icon: UserPlus });
  if (user.role === "owner")
    items.push({ to: "/permissions", label: "Permission Center", icon: ShieldCheck });
  if (["owner", "operations_manager"].includes(user.role))
    items.push({ to: "/audit", label: "Audit Logs", icon: ScrollText });
  return items;
}

export default function AppLayout({ children }) {
  const { user, logout } = useAuth();
  const { can } = useAuth();
  const { t, lang, setLang, languages } = useI18n();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [pendingApprovals, setPendingApprovals] = useState(0);
  const isOwnerTheme = user.role === "owner";
  const nav = buildNav(user, can);

  useEffect(() => {
    if (user?.language && user.language !== lang) setLang(user.language);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.language]);

  const changeLanguage = (code) => {
    setLang(code);
    api.patch("/auth/me/language", { language: code }).catch(() => {});
  };

  useEffect(() => {
    if (!can("discounts_refunds", "approve")) return;
    const fetchPending = () => api.get("/approvals/pending-count").then((r) => setPendingApprovals(r.data.pending)).catch(() => {});
    fetchPending();
    const t = setInterval(fetchPending, 45000);
    return () => clearInterval(t);
  }, [can]);

  const sidebarBg = isOwnerTheme ? "bg-navy text-white" : "bg-white text-slate-700 border-r border-slate-200";
  const accent = isOwnerTheme ? "text-gold" : "text-blue-600";

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const NavItems = () => (
    <nav className="flex flex-col gap-1 px-3">
      {nav.map((item) => {
        const Icon = item.icon;
        return (
          <NavLink
            key={item.to}
            to={item.to}
            data-testid={`nav-${item.to.slice(1)}`}
            onClick={() => setOpen(false)}
            className={({ isActive }) =>
              `nav-link flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium ${
                isActive
                  ? isOwnerTheme
                    ? "bg-white/10 text-white"
                    : "bg-blue-50 text-blue-700"
                  : isOwnerTheme
                  ? "text-white/60 hover:text-white hover:bg-white/5"
                  : "text-slate-600 hover:text-slate-900 hover:bg-slate-100"
              }`
            }
          >
            <Icon className="h-[18px] w-[18px]" strokeWidth={2} />
            <span className="flex-1">{t(`nav.${NAV_KEYS[item.to]}`, item.label)}</span>
            {item.to === "/approvals" && pendingApprovals > 0 && (
              <span data-testid="approvals-pending-badge" className="min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-semibold flex items-center justify-center">{pendingApprovals}</span>
            )}
          </NavLink>
        );
      })}
    </nav>
  );

  const initials = (user.full_name || user.username || "?")
    .split(" ")
    .map((s) => s[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

  return (
    <div className={`${isOwnerTheme ? "theme-owner bg-ivory" : "bg-slate-50"} min-h-screen flex`}>
      {/* Desktop sidebar */}
      <aside className={`hidden lg:flex flex-col w-64 shrink-0 ${sidebarBg}`}>
        <div className="flex items-center gap-3 px-5 h-16 shrink-0">
          <div className={`h-9 w-9 rounded-lg flex items-center justify-center ${isOwnerTheme ? "bg-gold" : "bg-navy"}`}>
            <Building2 className={`h-5 w-5 ${isOwnerTheme ? "text-navy" : "text-gold"}`} />
          </div>
          <div>
            <p className="font-heading font-semibold leading-none">Dental OS</p>
            <p className={`text-[10px] mt-1 ${isOwnerTheme ? "text-white/40" : "text-slate-400"}`}>Istanbul Clinic</p>
          </div>
        </div>
        <div className="mt-4 flex-1 overflow-y-auto">
          <NavItems />
        </div>
        <div className={`p-3 border-t ${isOwnerTheme ? "border-white/10" : "border-slate-200"}`}>
          <div className="flex items-center gap-3 px-2 py-2">
            <div className={`h-9 w-9 rounded-full flex items-center justify-center text-xs font-semibold ${isOwnerTheme ? "bg-gold text-navy" : "bg-navy text-white"}`}>
              {initials}
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium truncate flex items-center gap-1">
                {user.role === "owner" && <Crown className={`h-3.5 w-3.5 ${accent}`} />}
                {user.full_name}
              </p>
              <p className={`text-xs truncate ${isOwnerTheme ? "text-white/40" : "text-slate-400"}`}>
                {t(`roles.${user.role}`, ROLE_LABELS[user.role] || user.role)}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-1 px-2 py-1.5" data-testid="language-switcher">
            {languages.map((l) => (
              <button
                key={l.code}
                data-testid={`lang-${l.code}`}
                onClick={() => changeLanguage(l.code)}
                className={`px-2 py-1 rounded text-xs font-medium transition-colors ${
                  lang === l.code
                    ? isOwnerTheme ? "bg-white/15 text-white" : "bg-blue-50 text-blue-700"
                    : isOwnerTheme ? "text-white/50 hover:text-white" : "text-slate-500 hover:text-slate-900"
                }`}
              >
                {l.label}
              </button>
            ))}
          </div>
          <Button
            onClick={handleLogout}
            data-testid="logout-button"
            variant="ghost"
            className={`w-full justify-start gap-2 mt-1 ${isOwnerTheme ? "text-white/70 hover:text-white hover:bg-white/10" : "text-slate-600 hover:bg-slate-100"}`}
          >
            <LogOut className="h-4 w-4" /> {t("common.signOut")}
          </Button>
        </div>
      </aside>

      {/* Mobile drawer */}
      {open && (
        <div className="lg:hidden fixed inset-0 z-40 flex">
          <div className="absolute inset-0 bg-black/40" onClick={() => setOpen(false)} />
          <aside className={`relative w-64 ${sidebarBg} flex flex-col`}>
            <div className="flex items-center justify-between px-5 h-16">
              <span className="font-heading font-semibold">Dental OS</span>
              <button onClick={() => setOpen(false)} data-testid="close-drawer">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="mt-4 flex-1 overflow-y-auto">
              <NavItems />
            </div>
            <div className={`p-3 border-t ${isOwnerTheme ? "border-white/10" : "border-slate-200"}`}>
              <div className="flex items-center gap-1 px-1 py-1.5 mb-1" data-testid="language-switcher-mobile">
                {languages.map((l) => (
                  <button key={l.code} onClick={() => changeLanguage(l.code)}
                    className={`px-2 py-1 rounded text-xs font-medium ${lang === l.code ? (isOwnerTheme ? "bg-white/15 text-white" : "bg-blue-50 text-blue-700") : (isOwnerTheme ? "text-white/50" : "text-slate-500")}`}>
                    {l.label}
                  </button>
                ))}
              </div>
              <Button onClick={handleLogout} variant="ghost" className="w-full justify-start gap-2">
                <LogOut className="h-4 w-4" /> {t("common.signOut")}
              </Button>
            </div>
          </aside>
        </div>
      )}

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="lg:hidden flex items-center justify-between h-14 px-4 bg-white border-b border-slate-200">
          <button onClick={() => setOpen(true)} data-testid="open-drawer">
            <Menu className="h-6 w-6 text-slate-700" />
          </button>
          <span className="font-heading font-semibold text-navy">Dental OS</span>
          <NotificationBell variant="mobile" />
        </header>
        <header className="hidden lg:flex items-center justify-end h-14 px-6 bg-white border-b border-slate-200">
          <NotificationBell variant="desktop" />
        </header>
        <main className="flex-1 overflow-y-auto">{children}</main>
      </div>
    </div>
  );
}
